import React, { useCallback, useMemo } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { Checkbox, CheckboxProps } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useFeatureFlag } from 'modules/common/hooks';

type ControlledCheckboxProps = {
  name: string;
  disabled?: boolean;
  checkboxColor?: CheckboxProps['color'];
  defaultCheckedValue?: boolean;
  overideOnChange?: (event: any) => void;
};

const useStyles = makeStyles((theme) => ({
  disabled: { opacity: 0.4 },
  root: {
    padding: theme.spacing(0.875, 1.125),
    transform: 'scale(1.12)'
  }
}));

export const ControlledCheckbox: React.FC<ControlledCheckboxProps> = ({
  name,
  disabled,
  checkboxColor,
  defaultCheckedValue = false,
  overideOnChange
}: ControlledCheckboxProps) => {
  const classes = useStyles();
  const { control, setValue, getValues } = useFormContext();
  const onChange = useCallback(
    (event) => {
      setValue(name, event.target.checked, {
        shouldDirty: true
      });
    },
    [setValue, name]
  );
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const inputProps = useMemo(
    () => ({
      inputProps: {
        'data-testid': `clickable_checkbox_${name}`,
        'aria-label': isTiwScreenReaderEnabled ? 'checkbox' : ''
      } as any
    }),
    [isTiwScreenReaderEnabled, name]
  );

  useWatch({ name, control });

  const isChecked = useMemo(
    () =>
      overideOnChange
        ? defaultCheckedValue
        : getValues()[name] ?? defaultCheckedValue,
    [overideOnChange, getValues, defaultCheckedValue, name]
  );

  return (
    <Checkbox
      color={checkboxColor ?? 'primary'}
      classes={classes}
      onChange={useMemo(() => (overideOnChange ? overideOnChange : onChange), [
        onChange,
        overideOnChange
      ])}
      checked={isChecked}
      data-testid={`checkbox_${name}`}
      {...inputProps}
      disabled={disabled}
    />
  );
};
